import { Controller } from "@hotwired/stimulus"

// Sizes a scroll box so the card around it ends at the bottom of the visible page: everything
// above the box in the card (toolbar, column headers) and below it (totals) stays on screen, and
// only the box scrolls (position_grid/_table). The page keeps scrolling for what lies outside the
// card — the header above, a Schlusstext below.
//
// "The page" is the nearest scrolling ancestor — the layout's <main>, not the window. The height
// is measured from where the box sits with that page scrolled to the top, so it does not change
// while the page scrolls, and re-measured when the page or its content changes size (an
// Einleitungstext opened above, a notice in the toolbar). Below `min` there is no room for a
// useful box: it keeps that height and the page scrolls instead.
//
// `keep` names what should stay in view under the card as well — an Angebot's Schlusstext, which
// would otherwise sit unseen below a grid that fills the window. Its room is reserved up to
// `keepMax`; a longer text shows its beginning and the page scrolls to the rest.
export default class extends Controller {
  static values = {
    card: String,
    keep: String,
    keepMax: { type: Number, default: 160 },
    gap: { type: Number, default: 16 },
    min: { type: Number, default: 320 },
  }

  connect() {
    this.page = scrollParent(this.element)
    this.measure = this.measure.bind(this)
    this.observer = new ResizeObserver(() => this.schedule())
    this.observer.observe(this.page === document.scrollingElement ? document.body : this.page)
    Array.from(this.page.children).forEach((child) => this.observer.observe(child))
    window.addEventListener("resize", this.measure)
    this.measure()
  }

  disconnect() {
    this.observer.disconnect()
    window.removeEventListener("resize", this.measure)
    cancelAnimationFrame(this.frame)
  }

  schedule() {
    cancelAnimationFrame(this.frame)
    this.frame = requestAnimationFrame(this.measure)
  }

  measure() {
    const pageTop =
      this.page === document.scrollingElement ? 0 : this.page.getBoundingClientRect().top
    const box = this.element.getBoundingClientRect()
    const card = (this.cardValue && this.element.closest(this.cardValue)) || this.element
    const below = card.getBoundingClientRect().bottom - box.bottom
    const top = box.top - pageTop + this.page.scrollTop
    const reserved = this.reserved(card)
    const height = Math.max(
      this.minValue,
      this.page.clientHeight - top - below - reserved - this.gapValue,
    )
    const value = `${Math.floor(height)}px`
    if (this.element.style.maxHeight !== value) this.element.style.maxHeight = value
  }

  // From the card's bottom to the bottom of what is kept in view, the spacing between them included.
  reserved(card) {
    const kept = this.keepValue && document.querySelector(this.keepValue)
    if (!kept) return 0

    const extent = kept.getBoundingClientRect().bottom - card.getBoundingClientRect().bottom
    return Math.min(this.keepMaxValue, Math.max(0, extent))
  }
}

function scrollParent(element) {
  for (
    let node = element.parentElement;
    node && node !== document.body;
    node = node.parentElement
  ) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node
  }
  return document.scrollingElement
}
